import { components } from "@securityjourney/svc-learner-ai/dist/gen/sj_admin";

import { retrieveAuthToken } from "../../utils/authService";

type BatchDeleteResponsesResponse =
  components["schemas"]["securityjourney.proto.learner_ai.BatchDeleteSJAdminResponsesResponse"];

export interface ChatSession {
  sessionId: string;
  tenant: string;
  createdAt?: string;
  updatedAt?: string;
  title: string;
  firstMessagePreview: string;
  latestMessagePreview: string;
  lastResponseId: string | null;
  messageCount: number;
}

export interface ChatSessionTenantCount {
  tenant: string;
  count: number;
}

export interface ChatSessionMessage {
  messageId: string;
  sessionId: string;
  turn: number;
  role: "user" | "assistant";
  content: string;
  createdAt?: string;
}

export interface ListChatSessionsResponse {
  sessions: ChatSession[];
  tenantCounts: ChatSessionTenantCount[];
  limit: number;
  hasMore: boolean;
}

export interface GetChatSessionResponse {
  session: ChatSession;
  messages: ChatSessionMessage[];
}

export type ChatChunk =
  | { type: "token"; token: string }
  | {
      type: "done";
      responseId: string;
      sessionId: string | null;
      inputTokens: number;
      outputTokens: number;
    }
  | { type: "error"; error: string };

const CHAT_URL = "/svc/learner-ai/chat";
const CHAT_SESSIONS_URL = "/svc/learner-ai/chat/sessions";

export interface StreamChatParams {
  tenantScopedAuthToken?: string;
  message: string;
  tenant: string;
  previousResponseId: string | null;
  sessionId: string | null;
  onChunk: (chunk: ChatChunk) => void;
  signal?: AbortSignal;
}

const authHeaders = async (tokenOverride?: string): Promise<HeadersInit> => {
  const token = tokenOverride ?? (await retrieveAuthToken());
  return {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
};

const normalizeSession = (session: Record<string, unknown>): ChatSession => {
  const firstMessagePreview = String(
    session.firstMessagePreview ?? session.first_message_preview ?? ""
  );
  const latestMessagePreview = String(
    session.latestMessagePreview ?? session.latest_message_preview ?? ""
  );
  const title =
    String(session.title ?? "").trim() || firstMessagePreview || latestMessagePreview || "New chat";

  return {
    sessionId: String(session.sessionId ?? session.session_id ?? ""),
    tenant: String(session.tenant ?? ""),
    createdAt: (session.createdAt ?? session.created_at) as string | undefined,
    updatedAt: (session.updatedAt ?? session.updated_at) as string | undefined,
    title,
    firstMessagePreview,
    latestMessagePreview,
    lastResponseId: (session.lastResponseId ?? session.last_response_id ?? null) as string | null,
    messageCount: Number(session.messageCount ?? session.message_count ?? 0),
  };
};

const normalizeMessage = (message: Record<string, unknown>): ChatSessionMessage => ({
  messageId: String(message.messageId ?? message.message_id ?? crypto.randomUUID()),
  sessionId: String(message.sessionId ?? message.session_id ?? ""),
  turn: Number(message.turn ?? 0),
  role: message.role === "assistant" ? "assistant" : "user",
  content: String(message.content ?? ""),
  createdAt: (message.createdAt ?? message.created_at) as string | undefined,
});

export const streamChat = async ({
  message,
  tenant,
  tenantScopedAuthToken,
  previousResponseId,
  sessionId,
  onChunk,
  signal,
}: StreamChatParams): Promise<void> => {
  const res = await fetch(CHAT_URL, {
    method: "POST",
    headers: await authHeaders(tenantScopedAuthToken),
    body: JSON.stringify({
      message,
      tenant,
      ...(previousResponseId ? { previous_response_id: previousResponseId } : {}),
      ...(sessionId ? { session_id: sessionId } : {}),
    }),
    signal,
  });

  if (!res.ok || !res.body) {
    throw new Error(`Chat request failed: ${res.status}`);
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buf = "";

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buf += decoder.decode(value, { stream: true });
    const lines = buf.split("\n");
    buf = lines.pop() ?? "";
    for (const line of lines) {
      const trimmed = line.trim();
      if (!trimmed) continue;
      try {
        const { result: obj } = JSON.parse(trimmed);

        if (obj.token !== undefined) {
          onChunk({ type: "token", token: obj.token });
        } else if (obj.done) {
          onChunk({
            type: "done",
            responseId: obj.done.responseId ?? obj.done.response_id,
            sessionId: obj.done.sessionId ?? obj.done.session_id ?? null,
            inputTokens: obj.done.inputTokens ?? obj.done.input_tokens ?? 0,
            outputTokens: obj.done.outputTokens ?? obj.done.output_tokens ?? 0,
          });
        } else if (obj.error) {
          onChunk({ type: "error", error: obj.error });
        }
      } catch {
        // partial / non-JSON line, skip
      }
    }
  }
};

export const batchDeleteResponses = async (responseIds: string[]): Promise<string[]> => {
  if (responseIds.length === 0) return [];
  const res = await fetch("/svc/learner-ai/sjadmin/responses/batch-delete", {
    method: "POST",
    headers: await authHeaders(),
    body: JSON.stringify({ responseIds }),
  });
  if (!res.ok) throw new Error(`Batch delete failed: ${res.status}`);
  const body: BatchDeleteResponsesResponse = await res.json();
  return body.responseIds ?? [];
};

export const listChatSessions = async (limit = 50): Promise<ListChatSessionsResponse> => {
  const params = new URLSearchParams({ limit: String(limit) });
  const res = await fetch(`${CHAT_SESSIONS_URL}?${params.toString()}`, {
    headers: await authHeaders(),
  });
  if (!res.ok) throw new Error(`List chat sessions failed: ${res.status}`);
  const body = await res.json();
  return {
    sessions: (body.sessions ?? []).map(normalizeSession),
    tenantCounts: (body.tenantCounts ?? body.tenant_counts ?? []).map(
      (count: Record<string, unknown>) => ({
        tenant: String(count.tenant ?? ""),
        count: Number(count.count ?? 0),
      })
    ),
    limit: Number(body.limit ?? limit),
    hasMore: Boolean(body.hasMore ?? body.has_more),
  };
};

export const getChatSession = async (sessionId: string): Promise<GetChatSessionResponse> => {
  const res = await fetch(`${CHAT_SESSIONS_URL}/${encodeURIComponent(sessionId)}`, {
    headers: await authHeaders(),
  });
  if (!res.ok) throw new Error(`Get chat session failed: ${res.status}`);
  const body = await res.json();
  return {
    session: normalizeSession(body.session ?? {}),
    messages: (body.messages ?? []).map(normalizeMessage),
  };
};

export const DEFAULT_CHAT_SESSION_TITLE = "New chat";

export function chatSessionDisplayTitle(
  session: Pick<ChatSession, "title" | "firstMessagePreview">
): string {
  return session.title?.trim() || session.firstMessagePreview?.trim() || DEFAULT_CHAT_SESSION_TITLE;
}
